import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPlan } from "@/hooks/useTenantPlan";

type Vars = Record<string, string | number>;
export type TranslationFunction = (key: string, fallback?: string, vars?: Vars) => string;

interface UiTranslationRow {
  key: string;
  pt_br: string;
}

interface UiOverrideRow {
  key: string;
  pt_br: string;
}

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (_, name) => {
    const v = vars[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

/**
 * useTranslation — fetches global translations + tenant overrides
 * and exposes a t(key, fallback?, vars?) function with cascading lookup:
 *   1. tenant override (only if feature ui_customization is enabled)
 *   2. global ui_translations.pt_br
 *   3. dev-provided fallback
 *   4. raw key (debug)
 */
export function useTranslation(): { t: TranslationFunction; isLoading: boolean } {
  const { currentTenant } = useAuth();
  // hasFeature is safe even pre-auth — returns false when no tenant.
  const { hasFeature } = useTenantPlan();
  const tenantId = currentTenant?.id;
  const customizationEnabled = hasFeature("ui_customization" as any);

  const { data: globals = [], isLoading: globalsLoading } = useQuery<UiTranslationRow[]>({
    queryKey: ["ui-translations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ui_translations")
        .select("key, pt_br");
      if (error) {
        console.error("[useTranslation] global fetch error:", error);
        return [];
      }
      return (data ?? []) as UiTranslationRow[];
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const { data: overrides = [], isLoading: overridesLoading } = useQuery<UiOverrideRow[]>({
    queryKey: ["ui-translation-overrides", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("ui_translation_overrides")
        .select("key, pt_br")
        .eq("tenant_id", tenantId);
      if (error) {
        console.error("[useTranslation] overrides fetch error:", error);
        return [];
      }
      return (data ?? []) as UiOverrideRow[];
    },
    enabled: !!tenantId && customizationEnabled,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const globalMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of globals) m.set(row.key, row.pt_br);
    return m;
  }, [globals]);

  const overrideMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of overrides) m.set(row.key, row.pt_br);
    return m;
  }, [overrides]);

  const t = useCallback<TranslationFunction>(
    (key, fallback, vars) => {
      const overridden = customizationEnabled ? overrideMap.get(key) : undefined;
      const text = overridden ?? globalMap.get(key) ?? fallback ?? key;
      return interpolate(text, vars);
    },
    [customizationEnabled, overrideMap, globalMap],
  );

  return { t, isLoading: globalsLoading || overridesLoading };
}
